import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { HqApiError, authApi as api } from "../shared/api/auth.js";
import { useT } from "../i18n/index.js";
import { ROUTES } from "./routes.js";
import { useSession } from "./session-store.js";

/**
 * Cổng phiên phía giao diện.
 *
 * ── Đây là ĐIỀU HƯỚNG, không phải bảo vệ ────────────────────────────────────
 *
 * Máy chủ mới là nơi ép: `SessionGuard` tra lại tư cách thành viên mỗi request, và
 * `PermissionGuard` chặn theo quyền. Trang này chỉ đưa người dùng tới đúng chỗ thay vì để họ đâm
 * vào một màn 401 rồi tự đoán.
 */
export function SessionGate({ children }: { children: ReactNode }) {
  const { session, status, setSession, clear } = useSession();
  const t = useT("shell");
  const location = useLocation();
  /** Lượt hỏi phiên hỏng vì MẠNG (không phải vì máy chủ trả lời). Tăng `attempt` để hỏi lại. */
  const [unreachable, setUnreachable] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (status !== "dang-tai") return;
    setUnreachable(false);
    const controller = new AbortController();
    api
      .me(controller.signal)
      .then(setSession)
      .catch((err: unknown) => {
        // Chỉ coi là « chưa đăng nhập » khi máy chủ NÓI vậy. Mạng hỏng mà đá người dùng ra màn đăng
        // nhập thì họ đăng nhập lại, lại hỏng, và không ai biết vấn đề là mạng.
        if (err instanceof HqApiError) clear();
        // Nhưng cũng không được treo « Đang kiểm tra phiên… » mãi (HQ-2026-004: chứng chỉ của
        // `api.bube-hq.com` chưa cấp xong ⇒ trang trắng, không một chữ nào nói vì sao).
        else if (!controller.signal.aborted) setUnreachable(true);
      });
    return () => controller.abort();
  }, [status, setSession, clear, attempt]);

  if (status === "dang-tai" && unreachable) {
    return (
      <div className="p-6 text-sm">
        <p>{t("sessionUnreachable")}</p>
        <button type="button" className="mt-3 underline" onClick={() => setAttempt((n) => n + 1)}>
          {t("sessionRetry")}
        </button>
      </div>
    );
  }
  if (status === "dang-tai") {
    return <p className="p-6 text-sm opacity-60">{t("sessionChecking")}</p>;
  }
  if (status === "chua-dang-nhap") {
    return <Navigate to={ROUTES.login} replace />;
  }

  // Còn mật khẩu tạm thì máy chủ chặn mọi đường khác — đưa thẳng tới chỗ đổi.
  if (session?.mustChangePassword && location.pathname !== ROUTES.changePassword) {
    return <Navigate to={ROUTES.changePassword} replace />;
  }

  return <>{children}</>;
}
